/** @format */

import React, { useState, useEffect, useRef } from "react";
import { useCachedBooks } from "../hooks/useCachedBooks";
import { useFavorites } from "../hooks/useFavorites";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { formatCurrency } from "../utils/formatUtils";
import { useLocation, useSearchParams } from "react-router-dom";
import CustomDropdown from "../components/CustomDropdown";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  BookOpenIcon,
  XMarkIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  PencilIcon,
  AcademicCapIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import BookReviews from "../components/BookReviews";
import BookDetailsModal from "../components/BookDetailsModal";
import BookCard from "../components/BookCard";
import { Book, CartItem } from "../types";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import AIChatButton from '../components/AIChatButton';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const Shop: React.FC = () => {
  const { currentUser } = useAuth();
  const { addToCart, cartItems } = useCart();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    books,
    loading,
    error,
    fetchBooks,
    searchBooks,
    getCategories,
    getPrograms,
    saveFiltersToCache,
    loadFiltersFromCache,
  } = useCachedBooks();

  // Track if we've already fetched books to prevent unnecessary re-fetches
  const hasFetchedBooks = useRef(false);

  const { isBookFavorited, toggleFavorite } = useFavorites();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [programs, setPrograms] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<
    "newest" | "price_low" | "price_high" | "name" | "rating" | "popular"
  >("newest");
  const [selectedBookForReviews, setSelectedBookForReviews] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedBookForDetails, setSelectedBookForDetails] =
    useState<Book | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [highlightedBookId, setHighlightedBookId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'books' | 'materials'>('books');
  const [displayedBooks, setDisplayedBooks] = useState<Book[]>([]);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const booksPerPage = 4;

  // Handle URL parameters for affiliate links and initialize filters from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    const bookParam = urlParams.get('book');
    const qParam = urlParams.get('q') || '';
    const catParam = urlParams.get('category') || 'all';
    const progParam = urlParams.get('program') || 'all';
    const minParam = urlParams.get('min');
    const maxParam = urlParams.get('max');
    const sortParam = (urlParams.get('sort') as typeof sortBy | null);
    const tabParam = urlParams.get('tab') || 'books';
    
    if (refCode) {
      setReferralCode(refCode);
      // Store referral code in localStorage for signup
      localStorage.setItem('referralCode', refCode);
    }
    
    if (bookParam) {
      setHighlightedBookId(bookParam);
      // Scroll to the book if it exists
      setTimeout(() => {
        const bookElement = document.getElementById(`book-${bookParam}`);
        if (bookElement) {
          bookElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight effect
          bookElement.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
          setTimeout(() => {
            bookElement.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
          }, 3000);
        }
      }, 1000);
    }
    // Initialize filters/search from URL
    if (qParam) setSearchTerm(qParam);
    if (catParam) setSelectedCategory(catParam);
    if (progParam) setSelectedProgram(progParam);
    setPriceRange({
      min: minParam ? Number(minParam) : 0,
      max: maxParam ? Number(maxParam) : 1000,
    });
    if (sortParam) setSortBy(sortParam);
    if (tabParam === 'books' || tabParam === 'materials') setActiveTab(tabParam);
    
    // Load activeTab from cache if not in URL
    if (tabParam === 'books' || tabParam === 'materials') {
      const cachedFilters = loadFiltersFromCache();
      if (cachedFilters?.activeTab && !tabParam) {
        setActiveTab(cachedFilters.activeTab);
      }
    }
  }, [location, loadFiltersFromCache]);



  // Load books progressively on mount
  useEffect(() => {
    if (!hasFetchedBooks.current) {
      const q = searchParams.get('q');
      if (q && q.trim()) {
        searchBooks(q);
      } else {
      if (currentUser?.program) {
        fetchBooks({ userProgram: currentUser.program });
      } else {
        fetchBooks();
        }
      }
      hasFetchedBooks.current = true;
    }
  }, [currentUser?.program]); // Run on mount and when program changes

  // Update displayed books when books array changes
  useEffect(() => {
    if (books.length > 0) {
      // Reset to first page when books change
      setCurrentPage(1);
      updateDisplayedBooks(1);
      if (!hasInitialLoad) {
        setHasInitialLoad(true);
      }
    }
  }, [books, hasInitialLoad]);

  // Reset pagination when sorting changes
  useEffect(() => {
    if (books.length > 0 && hasInitialLoad) {
      updateDisplayedBooks(1);
    }
  }, [sortBy]);

  // Reset pagination when tab changes
  useEffect(() => {
    if (books.length > 0 && hasInitialLoad) {
      setCurrentPage(1);
      updateDisplayedBooks(1);
    }
  }, [activeTab]);



  // Sync filter state to URL and cache filters when they change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchTerm.trim()) params.q = searchTerm.trim();
    if (selectedCategory !== 'all') params.category = selectedCategory;
    if (selectedProgram !== 'all') params.program = selectedProgram;
    if (priceRange.min > 0) params.min = String(priceRange.min);
    if (priceRange.max < 1000) params.max = String(priceRange.max);
    if (sortBy && sortBy !== 'newest') params.sort = sortBy;
    if (activeTab !== 'books') params.tab = activeTab;
    setSearchParams(params, { replace: true });
    
    // Cache filters when they change
    saveFiltersToCache({
      searchTerm,
      selectedCategory,
      selectedProgram,
      priceRange,
      sortBy,
      activeTab,
      userProgram: currentUser?.program
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedCategory, selectedProgram, priceRange.min, priceRange.max, sortBy, activeTab]); // Remove saveFiltersToCache from dependency array

  // Load categories and programs in parallel (don't block book loading)
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [cats, progs] = await Promise.all([
          getCategories(),
          getPrograms(),
        ]);
        setCategories(cats);
        setPrograms(progs);
      } catch (error) {
        console.error("Error loading filters:", error);
        // Set default values if filters fail to load
        setCategories([]);
        setPrograms([]);
      }
    };
    loadFilters();
  }, []); // Only run once on mount

  // Apply filters and search
  const applyFilters = () => {
    const filters: any = {};

    if (selectedCategory !== "all") filters.category = selectedCategory;
    if (selectedProgram !== "all") filters.program = selectedProgram;
    if (priceRange.min > 0 || priceRange.max < 1000) {
      filters.priceRange = priceRange;
    }

    // Always respect user's program if they have one set
    if (currentUser?.program) {
      filters.userProgram = currentUser.program;
    }

    // Reset pagination when applying new filters
    setCurrentPage(1);
    setDisplayedBooks([]);
    setHasInitialLoad(false);
    fetchBooks(filters);
  };

  // Handle filter changes
  const handleFilterChange = () => {
    // Reset pagination when filters change
    setCurrentPage(1);
    setDisplayedBooks([]);
    setHasInitialLoad(false);
    applyFilters();
  };



  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Reset pagination when searching
      setCurrentPage(1);
      setDisplayedBooks([]);
      setHasInitialLoad(false);
      searchBooks(searchTerm);
    } else {
      // Reset pagination when clearing search
      setCurrentPage(1);
      setDisplayedBooks([]);
      setHasInitialLoad(false);
      if (currentUser?.program) {
        fetchBooks({ userProgram: currentUser.program });
      } else {
        fetchBooks();
      }
    }
  };

  // Debounce search when typing
  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timer = setTimeout(() => {
      if (!hasFetchedBooks.current) return;
      if (trimmed.length >= 2) {
        setCurrentPage(1);
        searchBooks(trimmed);
      }
      if (trimmed.length === 0) {
        setCurrentPage(1);
        if (currentUser?.program) {
          fetchBooks({ userProgram: currentUser.program });
        } else {
          fetchBooks();
        }
      }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Sort books
  const getSortedBooks = () => {
    const sortedBooks = [...books];

    switch (sortBy) {
      case "price_low":
        return sortedBooks.sort((a, b) => a.amount - b.amount);
      case "price_high":
        return sortedBooks.sort((a, b) => b.amount - a.amount);
      case "name":
        return sortedBooks.sort((a, b) => a.title.localeCompare(b.title));
      case "rating":
        return sortedBooks.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case "popular":
        return sortedBooks.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
      case "newest":
      default:
        // Handle case where createdAt might not exist or might be a string
        return sortedBooks.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });
    }
  };

  // Pagination functions
  const updateDisplayedBooks = (page: number) => {
    const sortedBooks = getSortedBooks();
    const startIndex = (page - 1) * booksPerPage;
    const endIndex = startIndex + booksPerPage;
    setDisplayedBooks(sortedBooks.slice(startIndex, endIndex));
    setCurrentPage(page);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= getTotalPages()) {
      updateDisplayedBooks(page);
      // Scroll to top of content
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getTotalPages = () => {
    const sortedBooks = getSortedBooks();
    return Math.ceil(sortedBooks.length / booksPerPage);
  };

  const getCurrentPageInfo = () => {
    const sortedBooks = getSortedBooks();
    const startIndex = (currentPage - 1) * booksPerPage;
    const endIndex = Math.min(startIndex + booksPerPage, sortedBooks.length);
    return {
      start: startIndex + 1,
      end: endIndex,
      total: sortedBooks.length
    };
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedCategory("all");
    setSelectedProgram("all");
    setPriceRange({ min: 0, max: 1000 });
    setSearchTerm("");
    setCurrentPage(1);
    fetchBooks();
  };

  // Cart functions
  const handleAddToCart = (book: Book) => {
    const cartItem = {
      type: "book" as const,
      itemId: book.id,
      name: book.title,
      price: Number(book.amount), // Ensure price is a number
      image: book.coverImage,
    };
    addToCart(cartItem);
    toast.success(`${book.title} added to cart!`);
  };


  


  if (loading && books.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading books...</p>
        </div>
      </div>
    );
  }

  if (error && books.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpenIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error Loading Books
        </h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary inline-flex items-center"
        >
          <BookOpenIcon className="h-5 w-5 mr-2" />
          Try Again
        </button>
      </div>
    );
  }

  const sortedBooks = getSortedBooks();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            {/* Left - Text */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <ShoppingCartIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Book Shop
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Shop Books</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Learn & Grow
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Discover high-quality study books and materials tailored for your program. Ghana Cedis (GHS) supported.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <a
                  href="#shop-search"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-1.5" /> Start Searching
                </a>
                {!shouldShowIOSWebViewAlert() && cartItems.length > 0 && (
                  <Link
                    to="/app/cart"
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                  >
                    <ShoppingCartIcon className="h-4 w-4 mr-1.5" /> View Cart ({cartItems.length})
                  </Link>
                )}
              </div>
            </div>

            {/* Right - Visuals (desktop only) */}
            <div className="relative hidden xl:block">
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <BookOpenIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Curated Books</h3>
                      <p className="text-blue-200 text-sm">Tailored for your program</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <LightBulbIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Smart Filters</h3>
                      <p className="text-blue-200 text-sm">Find the right book fast</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pt-4 sm:pt-6 lg:pt-8 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="relative z-10">
          {/* Search and Controls */}
        <div className="bg-white border-b border-gray-100">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Title and Search */}
              <div className="flex-1">
                {activeTab === 'books' && (
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Books
                    </label>
                    <form className="relative" onSubmit={handleSearch}>
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                        placeholder="Search by title, author, or topic..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          type="button"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                    </form>
                </div>
                )}
              </div>

              {/* Filter Toggle and Cart */}
              <div className="flex items-center gap-4">
                {activeTab === 'books' && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                      showFilters 
                        ? 'bg-blue-600 text-white shadow-lg' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                >
                  <FunnelIcon className="h-5 w-5" />
                    <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                </button>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Title Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
                {activeTab === 'books' ? (
                  <>
                    <BookOpenIcon className="h-10 w-10 text-blue-600" />
                    Book Shop
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-10 w-10 text-indigo-600" />
                    Study Materials
                  </>
                )}
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-4 max-w-2xl mx-auto">
                {activeTab === 'books' 
                  ? 'Discover high-quality study books tailored for your academic program'
                  : 'Access comprehensive study materials, practice tests, and learning resources'
                }
              </p>
              {currentUser?.program && activeTab === 'books' && (
                <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-blue-200">
                  <span className="text-sm text-gray-600">Program:</span>
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    {currentUser.program}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-gray-100">
          <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Tab Buttons */}
              <div className="flex space-x-2 bg-gray-50 p-1 rounded-xl w-full sm:w-auto">
                <button
                  onClick={() => setActiveTab('books')}
                  className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    activeTab === 'books'
                      ? 'bg-white text-blue-600 shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <BookOpenIcon className="h-5 w-5" />
                  Books
                </button>
                <button
                  onClick={() => setActiveTab('materials')}
                  className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    activeTab === 'materials'
                      ? 'bg-white text-blue-600 shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  Study Materials
                </button>
              </div>
              
              {/* Tab Description */}
              <div className="text-sm text-gray-600">
                {activeTab === 'books' ? (
                  <span>Browse and purchase study books for your program</span>
                ) : (
                  <span>Access comprehensive study materials and resources</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && activeTab === 'books' && (
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200 shadow-sm">
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Filter Options</h3>
                <p className="text-sm text-gray-600">Refine your search to find the perfect books</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {/* Category Filter */}
                <div>
                  <CustomDropdown
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    options={[
                      { value: "all", label: "All Categories" },
                      ...categories.map((category) => ({
                        value: category,
                        label: category
                      }))
                    ]}
                    placeholder="All Categories"
                    className="w-full"
                  />
                </div>

                {/* Program Filter */}
                <div>
                  <CustomDropdown
                    value={selectedProgram}
                    onChange={setSelectedProgram}
                    options={[
                      { 
                        value: "all", 
                        label: currentUser?.program ? `${currentUser.program} Only` : "All Programs" 
                      },
                      ...(currentUser?.program ? [] : programs.map((program) => ({
                        value: program,
                        label: program
                      })))
                    ]}
                    placeholder="All Programs"
                    className="w-full"
                    disabled={!!currentUser?.program}
                  />
                </div>

                {/* Price Range - Hidden on iOS WebView */}
                {!shouldShowIOSWebViewAlert() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price Range
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min || ""}
                        onChange={(e) =>
                          setPriceRange((prev) => ({
                            ...prev,
                            min: Number(e.target.value) || 0,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max || ""}
                        onChange={(e) =>
                          setPriceRange((prev) => ({
                            ...prev,
                            max: Number(e.target.value) || 1000,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>
                )}

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <CustomDropdown
                    value={sortBy}
                    onChange={(value) => setSortBy(value as any)}
                    options={[
                      { value: "newest", label: "Newest First" },
                      ...(shouldShowIOSWebViewAlert() ? [] : [
                        { value: "price_low", label: "Price: Low to High" },
                        { value: "price_high", label: "Price: High to Low" }
                      ]),
                      { value: "name", label: "Name A-Z" },
                      { value: "rating", label: "Highest Rated" },
                      { value: "popular", label: "Most Reviewed" }
                    ]}
                    placeholder="Sort by"
                    className="w-full"
                  />
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <button
                    onClick={resetFilters}
                    className="w-full px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200"
                  >
                    Clear All
                  </button>
                </div>

                {/* Apply Filters */}
                <div className="flex items-end">
                  <button
                    onClick={applyFilters}
                    className="w-full px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {sortedBooks.length} {sortedBooks.length === 1 ? "Book" : "Books"}{" "}
              Found
            </h2>
            {currentUser?.program && (
              <p className="text-sm text-blue-600 mt-1">
                Showing {currentUser.program} program books
              </p>
            )}
            {Object.values({
              searchTerm,
              selectedCategory,
              selectedProgram,
              priceRange,
            }).some(
              (value) =>
                value !== "" &&
                value !== "all" &&
                !(
                  typeof value === "object" &&
                  value.min === 0 &&
                  value.max === 1000
                )
            ) && (
              <p className="text-sm text-gray-500 mt-1">
                Additional filters applied
              </p>
            )}
          </div>
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm("");
                fetchBooks();
              }}
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              Clear search
            </button>
          )}
        </div>

        {/* Error Display */}
        {error && activeTab === 'books' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}



        {/* Books Grid */}
        {activeTab === 'books' && (
          <>
        {loading ? (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 text-gray-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span>Loading books...</span>
                  </div>
                </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
              <div
                key={i}
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse"
              >
                      <div className="bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl h-48 mb-4"></div>
                <div className="space-y-3">
                        <div className="bg-gray-200 h-6 rounded-lg"></div>
                        <div className="bg-gray-200 h-4 rounded-lg w-3/4"></div>
                        <div className="bg-gray-200 h-4 rounded-lg w-1/2"></div>
                        <div className="flex gap-2 mt-4">
                          <div className="bg-gray-200 h-8 rounded-lg w-20"></div>
                          <div className="bg-gray-200 h-8 rounded-lg w-16"></div>
                        </div>
                </div>
              </div>
            ))}
                </div>
          </div>
        ) : error ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-red-100">
                <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-8">
                  <BookOpenIcon className="h-12 w-12 text-red-600" />
            </div>
                <div className="text-red-600 text-2xl font-bold mb-3">
                  Error Loading Books ❌
            </div>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">{error}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg"
            >
              Try Again
            </button>
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("all");
                      setSelectedProgram("all");
                      setPriceRange({ min: 0, max: 1000 });
                      setSortBy("newest");
                    }}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold"
                  >
                    Reset Filters
            </button>
          </div>
            </div>
            ) : displayedBooks.length === 0 && !loading ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-8">
                  <BookOpenIcon className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
                  <BookOpenIcon className="h-6 w-6 text-gray-500" />
                  No Books Found
            </h3>
                <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                  {searchTerm 
                    ? `We couldn't find any books matching "${searchTerm}". Try different keywords or browse all books.`
                    : "We couldn't find any books matching your current filters. Try adjusting your search criteria."
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("all");
                      setSelectedProgram("all");
                      setPriceRange({ min: 0, max: 1000 });
                      setSortBy("newest");
                    }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 font-semibold"
                  >
                    Clear All Filters
                  </button>
                  <button
                    onClick={() => setShowFilters(true)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300 font-semibold"
                  >
                    Adjust Filters
                  </button>
                </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 w-full">
                  {displayedBooks.map((book) => (
                    <div
                      key={book.id}
                      id={`book-${book.id}`}
                      className={highlightedBookId === book.id ? 'ring-2 ring-blue-500 ring-opacity-50 rounded-3xl' : ''}
                    >
                      <BookCard
                        book={book}
                        onViewDetails={() => {
                          setSelectedBookForDetails(book);
                          setShowDetailsModal(true);
                        }}
                        onViewReviews={() => {
                          setSelectedBookForReviews({
                            id: book.id,
                            title: book.title,
                          });
                          setShowReviewsModal(true);
                        }}
                        onAddToCart={handleAddToCart}
                        layout="horizontal"
                      />
                    </div>
                  ))}
            </div>

                {/* Pagination */}
                {displayedBooks.length > 0 && getTotalPages() > 1 && (
                  <div className="text-center py-6">
                    <div className="text-sm text-gray-600 mb-4">
                      Showing {getCurrentPageInfo().start}-{getCurrentPageInfo().end} of {getCurrentPageInfo().total} books
                    </div>
                    
                    {/* Pagination Controls */}
                    <div className="flex items-center justify-center space-x-2">
                      {/* Previous Page Button */}
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                              page === currentPage
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>
                      
                      {/* Next Page Button */}
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === getTotalPages()}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
          </>
        )}
        </>
        )}

        {/* Study Materials Tab */}
        {activeTab === 'materials' && (
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 text-center shadow-sm border border-gray-100">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
              <DocumentTextIcon className="h-12 w-12 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              Study Materials Coming Soon!
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              We're working hard to bring you comprehensive study materials, practice tests, 
              flashcards, and learning resources. This will include everything you need to 
              excel in your studies beyond just books.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <PencilIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-blue-900 mb-2">Practice Tests</h4>
                <p className="text-sm text-blue-700">Mock exams and quizzes to test your knowledge</p>
              </div>
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <AcademicCapIcon className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="font-semibold text-green-900 mb-2">Study Guides</h4>
                <p className="text-sm text-green-700">Comprehensive summaries and key points</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <LightBulbIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="font-semibold text-purple-900 mb-2">Flashcards</h4>
                <p className="text-sm text-purple-700">Quick revision cards for key concepts</p>
              </div>
            </div>
            <div className="mt-8">
              <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg">
                Get Notified When Available
              </button>
            </div>
          </div>
        )}

        {/* Call to Action Section */}
        {!currentUser && activeTab === 'books' && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 sm:p-8 text-center text-white mb-8 sm:mb-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Shopping?</h2>
            <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
              Create an account to save favorites, track orders, and get
              personalized recommendations for your ICAG and CITG studies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Create Free Account
              </Link>
              <Link
                to="/signin"
                className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}

        {/* Reviews Modal */}
        {showReviewsModal && selectedBookForReviews && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setShowReviewsModal(false)}
              />

              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Reviews for {selectedBookForReviews.title}
                  </h3>
                  <button
                    onClick={() => setShowReviewsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Reviews Content */}
                <BookReviews
                  bookId={selectedBookForReviews.id}
                  bookTitle={selectedBookForReviews.title}
                />
              </div>
            </div>
          </div>
        )}

        {/* Book Details Modal */}
        <BookDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedBookForDetails(null);
          }}
          book={selectedBookForDetails}
        />

        </div>
      </div>
      
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default Shop;
